import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createChart, IChartApi, ISeriesApi } from 'lightweight-charts';

import {
  postComment,
  fetchMarketDetails,
  fetchChartData,
  fetchTradingHistory,
  fetchComments,
  fetchHolderDistribution,
  fetchTradingHistoryAndCommentsSummary,
  purchaseToken,
  postSentiment,
  sendTokensToFriend,
  sendTokensToDeveloper,
  sellToken
} from '../services/api';

import {
  Market,
  Comment,
  ChartData,
  ToastMessage,
  TradingHistoryItem,
  HolderDistribution,
  PurchaseTokenPayload,
  SendTokenBody
} from '../types';

import '../styles/TokenPage.css';
import '../styles/Toast.css';

import Toast from '../components/Toast';
import { Modal } from '../components/Modal';
import SentimentIndicator from '../components/SentimentIndicator';

import {truncateAddress, percentageOfOwnership} from '../utils/utils'

import { useAuthContext } from "../contexts/AuthContext";

import nutIcon from '../assets/nut.svg';
import game7Icon from '../assets/game7.png';

const TokenPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [market, setMarket] = useState<Market | null>(null);
  const [chartData, setChartData] = useState<ChartData[]>([]);

  const [tradingHistory, setTradingHistory] = useState<TradingHistoryItem[]>([]);
  const [tradePage, setTradePage] = useState(1);
  const [loadingMoreTrades, setLoadingMoreTrades] = useState(false);

  const [comments, setComments] = useState<Comment[]>([]);
  const [commentPage, setCommentPage] = useState(1);
  const [loadingMoreComments, setLoadingMoreComments] = useState(false);

  const [holderDistribution, setHolderDistribution] = useState<HolderDistribution[]>([]);

  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  const [activeTab, setActiveTab] = useState<'comments' | 'trading'>('comments');
  const [error, setError] = useState<string | null>(null);

  const [commentText, setCommentText] = useState('');
  const [tradeMode, setTradeMode] = useState<'buy' | 'sell'>('buy');
  const [inputToken, setInputToken] = useState<'$G7' | 'GAME'>('GAME');
  const [inputSlippage, setInputSlippage] = useState<string>('4%');
  const [showSlippageModal, setShowSlippageModal] = useState(false);
  const [showSendToDevModal, setShowSendToDevModal] = useState(false);
  const [showSendToFriendModal, setShowSendToFriendModal] = useState(false);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [shakeModal, setShakeModal] = useState(false);
  const [marketSentiment, setMarketSentiment] = useState(50);

  const [tradeAmount, setTradeAmount] = useState<string>('');
  const [showSellModal, setShowSellModal] = useState(false);
  const [userBalance, setUserBalance] = useState<number>(1000); // Simulated user balance

  const [sendFriendAmount, setSendFriendAmount] = useState<string>('');
  const [sendFriendAddress, setSendFriendAddress] = useState<string>('');
  const [sendDevAmount, setSendDevAmount] = useState<string>('');

  const { user, isAuthenticated } = useAuthContext();

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);

  const isInitialRender = useRef(true);
  const isFetching = useRef(false);
  const lastFetchedId = useRef<string | null>(null);

  const fetchTokenData = useCallback(async (tokenId: string) => {
    if (isFetching.current || tokenId === lastFetchedId.current) return;

    try {
      isFetching.current = true;
      setLoading(true);
      setError(null);

      const [marketData, chartData, tradingHistoryCommentsData, holderDistributionData] = await Promise.all([
        fetchMarketDetails(String(tokenId)),
        fetchChartData(tokenId),
        fetchTradingHistoryAndCommentsSummary(tokenId),
        fetchHolderDistribution(tokenId)
      ]);


      if (marketData) {
        setMarket(marketData);
        setMarketSentiment(marketData.TraderSentiment);
        setChartData(chartData);
        setTradingHistory(tradingHistoryCommentsData.trades);
        setComments(tradingHistoryCommentsData.comments);
        setHolderDistribution(holderDistributionData);
        lastFetchedId.current = tokenId;
      } else {
        setError('Token not found');
        navigate('/not-found', {replace: true});
      }

    } catch (err) {
      setError('Failed to load data');
      console.error('Error fetching token data:', err);
    } finally {
      setLoading(false);
      isFetching.current = false;
    }
  }, [navigate]);

  useEffect(() => {
    // Only fetch on the initial render
    if (isInitialRender.current && id) {
      isInitialRender.current = false;
      fetchTokenData(id);
    }

    // Cleanup function
    return () => {
      // Cleanup function - reset flags when component unmounts
      isInitialRender.current = true;
      isFetching.current = false;
    };
  }, [id, fetchTokenData]);

  useEffect(() => {
    if (chartContainerRef.current && chartData.length > 0) {
      const handleResize = () => {
        chartRef.current?.applyOptions({ width: chartContainerRef.current?.clientWidth });
      };

      chartRef.current = createChart(chartContainerRef.current, {
        width: chartContainerRef.current.clientWidth,
        height: 660,
        layout: {
          background: { color: '#151220' },
          textColor: '#d1d4dc',
        },
        grid: {
          vertLines: { color: '#292936' },
          horzLines: { color: '#292936' },
        },
        rightPriceScale: {
          borderColor: '#292936',
        },
        timeScale: {
          borderColor: '#292936',
          timeVisible: true,
          secondsVisible: false,
        },
      });

      seriesRef.current = chartRef.current.addCandlestickSeries({
        upColor: '#26a69a',
        downColor: '#ef5350',
        borderVisible: false,
        wickUpColor: '#26a69a',
        wickDownColor: '#ef5350'
      });

      seriesRef.current.setData(chartData);

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
        chartRef.current?.remove();
      };
    }
  }, [chartData]);

  const addToast = useCallback((message: string, type: 'success' | 'error' | 'info') => {
    const id = Date.now();
    setToasts(prevToasts => [...prevToasts, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prevToasts => prevToasts.filter(toast => toast.id !== id));
  }, []);

  const handleQuickAmountClick = (amount: number) => {
    if (tradeMode === 'buy') {
      setTradeAmount(amount.toString());
    } else {
      //let tradeAmount = eval(`${userBalance} * ${'.'+amount}.toString()`)
      let tradeAmount = (Number(userBalance) * Number(`.${amount}`))
      if (amount === 100) {
        tradeAmount = userBalance
      }

      setTradeAmount(tradeAmount.toString());
    }
  };

  const handleSlippageChange = (slippage: number) => {
    setInputSlippage(`${slippage}%`);
    setShowSlippageModal(false);
  };

  const renderQuickAmounts = () => {
    const amounts = tradeMode === 'buy'
      ? [100, 500, 1000, 5000]
      : [25, 50, 75, 100];

    return (
      <div className="quick-amounts">
        {amounts.map((amount, index) => (
          <button key={index} onClick={() => handleQuickAmountClick(amount)}>
            {tradeMode === 'buy'
              ? `${amount} ${inputToken === '$G7' ? '$G7' : market?.Symbol}`
              : `${amount}% ${market?.Symbol}`
            }
          </button>
        ))}
      </div>
    );
  };

  const handleCommentSubmit = () => {
    if (!id || !isAuthenticated || !user || user.address === null) {
      addToast('🙀 Please connect wallet to comment.', 'error');
      return;
    }

    postComment(id, user.address, commentText)
      .then( (response) => {
        const newComment: Comment = {
          WalletAddress: user.address,
          Created_at: new Date(),
          Comment: commentText
        };

        setComments(prevComments => [newComment, ...prevComments]);
        setCommentText('');
        addToast('Comment succeeded 🎉.', 'success');
      }).catch(() => addToast('Comment failed 😞.', 'error'));

    setCommentText('');
  };

  const handleSendToDev = () => {
    if (!isAuthenticated || !user) {
      addToast('🙀 Please connect wallet to send tokens to developers.', 'error')
      return;
    }
    setShowSendToDevModal(true);
  };

  const handleSendToFriend = () => {
    if (!isAuthenticated || !user) {
      addToast('🙀 Please connect wallet to send tokens to a friend.', 'error')
      return;
    }
    setShowSendToFriendModal(true);
  };

  const handleThumbsUp = () => {
    if (!id || !isAuthenticated || !user) {
      addToast('🙀 Please connect wallet to vote.', 'error');
      return;
    }
    postSentiment(id, user.address, 'bullish')
      .then((r) => {
        if(r) {
          setMarketSentiment(prevSentiment => Math.min(prevSentiment + 2, 100));
          addToast('Your 🐂 bullish sentiment has been recorded.', 'success');
        } else {
          addToast('🤭 Oops. You can only vote once.', 'error');
        }
      }).catch(() => addToast('Your 🐂 bullish sentiment failed.', 'error'))
  };

  const handleThumbsDown = () => {
    if (!id || !isAuthenticated || !user) {
      addToast('🙀 Please connect wallet to vote.', 'error');
      return;
    }

    postSentiment(id, user.address, 'bearish')
      .then((r) => {
        if(r) {
          setMarketSentiment(prevSentiment => Math.min(prevSentiment - 2, 100));
          addToast('Your 🐻 bearish sentiment has been recorded.', 'success');
        } else {
          addToast('🤭 Oops. You can only vote once.', 'error');
        }
      }).catch(() => addToast('Your 🐻 bearish sentiment failed.', 'error'))
  };

  const sendToFriend = () => {
    if (!id || !isAuthenticated || !user) {
      addToast('🙀 Please connect wallet to send tokens.', 'error');
      return;
    }

    let payload: SendTokenBody = {
      walletAddress: user.address,
      contractAddress: id,
      tokenQty: Number(sendFriendAmount),
      sendWalletAddress: sendFriendAddress,
    }

    sendTokensToFriend(id, payload)
      .then((r) => {
        if(r) {
          addToast('Success. Your friends 💰 just got bigger.', 'success');
        } else {
          addToast('🤭 Oops. You are maxed out.', 'error');
        }
      }).catch(() => addToast('🤭 Oops. Send tokens to a friend has failed.', 'error'))

    setSendFriendAmount('');
    setSendFriendAddress('');
  };

  const sendToDeveloper = () => {
    if (!id || !isAuthenticated || !user) {
      addToast('🙀 Please connect wallet to send tokens.', 'error');
      return;
    }

    let payload: SendTokenBody = {
      walletAddress: user.address,
      contractAddress: id,
      tokenQty: Number(sendDevAmount),
      sendWalletAddress: "",
    }

    sendTokensToDeveloper(id, payload)
      .then((r) => {
        if(r) {
          addToast('Success. The game developer just got a bigger 💰.', 'success');
        } else {
          addToast('🤭 Oops. You are maxed out.', 'error');
        }
      }).catch(() => addToast('🤭 Oops. Send tokens to the developer has failed.', 'error'))

    setSendDevAmount('');
  };

  const handleTradeModeChange = (mode: 'buy' | 'sell') => {
    setTradeMode(mode);
    setTradeAmount(''); // Reset trade amount when switching modes
  };

  const handleBuy = () => {
    if (id && user && isAuthenticated && tradeAmount && parseFloat(tradeAmount) > 0) {
      const payload: PurchaseTokenPayload = {
        symbol: "",
        slippage: inputSlippage || '4%',
        tokenQty: parseFloat(tradeAmount),
        contractAddress: id,
        walletAddress: user.address
      };

      purchaseToken(id, payload).then((r) => {
        if (r) {
          setShowPurchaseModal(true);
          setShakeModal(true);
          setTimeout(() => setShakeModal(false), 1000); // Remove shake class after animation
          // Update user balance (in a real app, this would be handled by your state management system)
          setUserBalance(prevBalance => prevBalance + parseFloat(tradeAmount));
        } else {
          addToast('Token purchased failed 😞.', 'error')
        }
      })
    } else if (!isAuthenticated) {
      addToast('🙀 Please connect your wallet to make a purchase.', 'error');
    } else {
      addToast('Please enter a valid amount to purchase 💰.', 'error');
    }

    setTradeAmount("");
  };

  const handleSell = () => {
    if (id && user && isAuthenticated && tradeAmount && parseFloat(tradeAmount) > 0) {
      const sellAmount = parseFloat(tradeAmount);

      if (sellAmount > userBalance) {
        addToast(`You don't have enough ${market?.Symbol} to sell. Your balance: ${userBalance}`, 'error');
        return;
      }

      const payload: PurchaseTokenPayload = {
        symbol: "",
        slippage: inputSlippage || '4%',
        tokenQty: sellAmount,
        contractAddress: id,
        walletAddress: user.address
      };

      sellToken(id, payload).then((r) => {
        if (r) {
          setShowSellModal(true);
          setShakeModal(true);
          setTimeout(() => setShakeModal(false), 1000); // Remove shake class after animation
          // Update user balance (in a real app, this would be handled by your state management system)
          setUserBalance(prevBalance => prevBalance - sellAmount);
        } else {
          addToast('Sale of Token failed 😞.', 'error')
        }
      })
    } else if (!isAuthenticated) {
      addToast('🙀 Please connect your wallet to sell.', 'error');
    } else {
      addToast('Please enter a valid amount to sell 💰.', 'error');
    }

    setTradeAmount("");
  };

  const handleShareOnTwitter = (action: 'bought' | 'sold' | 'share') => {
    let tweetText = encodeURIComponent(`I just ${action} ${tradeAmount} ${market?.Symbol} on Game7 Swap! #Game7 #Crypto`);

    if (action === 'share') {
      tweetText = encodeURIComponent(`Check out the ${market?.Name} Meme Coin on Weirdly.fuN: https://weirdly.fun/game/${id}`);
    }

    const twitterUrl = `https://twitter.com/intent/tweet?text=${tweetText}`;
    window.open(twitterUrl, '_blank');
  };

  const handleBuyG7Token = (url: string) => {
    window.open(url, '_blank');
  };

  const loadMoreComments = useCallback(async () => {
    if (!id || loadingMoreComments) return;

    setLoadingMoreComments(true);
    try {
      const newComments = await fetchComments(id, commentPage + 1);
      if (newComments && newComments.length > 0) {
        setComments(prevComments => [...prevComments, ...newComments]);
        setCommentPage(prevPage => prevPage + 1);
      }
    } catch (error) {
      console.error('Error fetching more comments:', error);
    } finally {
      setLoadingMoreComments(false);
    }
  }, [id, commentPage, loadingMoreComments]);

  const loadMoreTrades = useCallback(async () => {
    if (!id || loadingMoreTrades) return;

    setLoadingMoreTrades(true);
    try {
      const newTrades = await fetchTradingHistory(id, tradePage + 1);
      if (newTrades && newTrades.length > 0) {
        setTradingHistory(prevTrades => [...prevTrades, ...newTrades]);
        setTradePage(prevPage => prevPage + 1);
      }
    } catch (error) {
      console.error('Error fetching more trades:', error);
    } finally {
      setLoadingMoreTrades(false);
    }
  }, [id, tradePage, loadingMoreTrades]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!market) return <div>Market not found</div>;

  // @ts-ignore
  return (
    <div className="profile-page">
      <div className="profile-header">
        <img src={`../game-images/${market.LogoUrl}`} alt={market.Name} className="profile-logo" />
        <div className="profile-info">
          <h1>{market.Name} (${market.Symbol})</h1>
          <p className="contract-info">Contract: {market.ContractAddress}</p>
          <div className="profile-tags">
            { market.isListedOnSwap === 1 &&
              <button onClick={() => handleBuyG7Token(`https://g7swap.io/${id}`)} className="action-button-listing">
                Listed on G7 Swap
              </button>
            }
            { market.isListedOnSwap === 0 &&
              <button className="action-button-not-listed">
                Not Listed on G7 Swap
              </button>
            }

            <button className="action-button-listing">
              Boost APY: 59.09%
            </button>
          </div>
          <p className="profile-description">{market.Description}</p>
        </div>
        <div className="sentiment-and-actions">
          <SentimentIndicator sentiment={marketSentiment} />
          <div className="action-buttons">
            <button onClick={() => handleShareOnTwitter('share')} className="action-button share-twitter">
              Share on X
            </button>
            <button onClick={() => handleBuyG7Token(`${market.StoreUrl}`)} className="action-button buy-g7">
              Play this game
            </button>
          </div>
        </div>
      </div>

      <div className="profile-description">
        <small>
          Notice: {market.Name} is not affiliated, in any way, with the meme coin mentioned on this page. Coins are provided for your Entertainment and we hope you find them Weirdly Fun.
        </small>
      </div>
      <div className="profile-stats">
        <div className="stat-item">
          <span className="stat-label">Price</span>
          <span className="stat-value">{market.CurrentPrice} $G7 <span className="stat-change">{market.PercentageChange}%</span></span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Marketcap</span>
          <span className="stat-value">${market.MarketCap.toLocaleString()} <span className="stat-change">{market.MarketCapChange}%</span></span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Liquidity</span>
          <span className="stat-value">${(market.CurrentPrice * 113698467.17).toLocaleString()}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">24H Volume</span>
          <span className="stat-value">{market.Volume.toLocaleString()} $G7</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Token Created</span>
          <span className="stat-value">{market.LaunchTime.toLocaleString()}</span>
        </div>
      </div>

      <div className="profile-main-content">
        <div className="profile-chart-container">
          <h2 className="chart-title">{market.Name} (${market.Symbol}) / $G7</h2>
          <div ref={chartContainerRef} className="chart"></div>
        </div>

        <div className="trade-and-bonding">
          <div className="trade-interface">
            <div className="trade-tabs">
              <button
                className={`tab ${tradeMode === 'buy' ? 'active' : ''}`}
                onClick={() => handleTradeModeChange('buy')}
              >
                Buy
              </button>
              <button
                className={`tab ${tradeMode === 'sell' ? 'active' : ''}`}
                onClick={() => handleTradeModeChange('sell')}
              >
                Sell
              </button>
            </div>
            <div className="trade-switch">
              { tradeMode !== 'sell' && (
                <button onClick={() => setInputToken(inputToken === '$G7' ? 'GAME' : '$G7')} className="btn-switch">
                  <span>Switch to {inputToken === '$G7' ? market.Symbol : '$G7'}</span>
                </button>
              )}
              { tradeMode === 'sell' && (
                <button onClick={() => setInputToken('GAME')} className="btn-switch">
                  <span>Can't switch to $G7</span>
                </button>
              )}
              <button onClick={() => setShowSlippageModal(true)} className="btn-slippage" title="set slippage">
                <img src={nutIcon} alt="Slippage" />
              </button>
            </div>
            <div className="trade-form">
              <div className="trade-input-wrapper">
                <input
                  type="text"
                  placeholder="Enter the amount"
                  className="trade-input"
                  value={tradeAmount}
                  onChange={(e) => setTradeAmount(e.target.value)}
                />
                <span className="trade-input-token">
                  {tradeMode === 'buy' ? (inputToken === '$G7' ? '$G7' : market.Symbol) : market.Symbol}
                </span>
                {tradeMode === 'buy' && inputToken === '$G7' && (
                  <img src={game7Icon} alt="$G7" className="trade-input-icon-alt" />
                )}
                {tradeMode === 'buy' && inputToken === 'GAME' && (
                  <img src={`../game-images/${market.LogoUrl}`} alt="$G7" className="trade-input-icon" />
                )}
                {tradeMode === 'sell' && (
                  <img src={`../game-images/${market.LogoUrl}`} alt={market.Symbol} className="trade-input-icon" />
                )}
              </div>
              <div className="trade-balance">
                <span>
                  Balance: {user ? userBalance : '--'} {tradeMode === 'buy' ? (inputToken === '$G7' ? '$G7' : '$'+market.Symbol) : '$'+market.Symbol}
                </span>
                <span>
                  Slippage (%): {inputSlippage}
                </span>
              </div>
              {renderQuickAmounts()}
               <button
                  className="trade-button"
                  onClick={tradeMode === 'buy' ? handleBuy : handleSell}
                >
                  {tradeMode === 'buy' ? `Buy ${inputToken === '$G7' ? '$G7' : '$'+market.Symbol}` : `Sell $${market.Symbol}`}
                </button>
            </div>
          </div>

          <div className="action-buttons-row">
            <button onClick={handleSendToDev} className="action-button">Send <span>${market.Symbol}</span> <br />to Devs</button>
            <button onClick={handleSendToFriend} className="action-button">Send <span>${market.Symbol}</span> <br />to Friend</button>
            <button onClick={handleThumbsUp} className="action-button thumbs-up">👍 <br />Bullish</button>
            <button onClick={handleThumbsDown} className="action-button thumbs-down">👎 <br />Bearish</button>
          </div>

          <div className="bonding-curve-info">
            <h3>Bonding Curve Progress: {`${market.BondingCurveProgress}%`}</h3>
            <div className="progress-bar">
              <div className="progress" style={{width: `${market.BondingCurveProgress}%`}}></div>
            </div>
            <p>
              There are <span className="purple-text">113,674,579.48 {market.Symbol}</span> still available for sale in
              the bonding curve and there are <span className="purple-text">62,608.76 $G7</span> in the bonding curve.
            </p>
            <p>
              When the market cap reaches <span className="purple-text">$77,708.91</span> all the liquidity from the
              bonding curve will be deposited into G7 Swap and burned. Progression increases as the price goes up.
            </p>
          </div>
        </div>
      </div>

      <div className="profile-secondary-content">
        <div className="profile-additional-info">
          <div className="tabs">
            <button
              className={`tab ${activeTab === 'comments' ? 'active' : ''}`}
              onClick={() => setActiveTab('comments')}
            >
              Comments
            </button>
            <button
              className={`tab ${activeTab === 'trading' ? 'active' : ''}`}
              onClick={() => setActiveTab('trading')}
            >
              Trading History
            </button>
          </div>

          {activeTab === 'comments' && (
            <div className="comments-section">
              <div className="comment-input">
                <textarea
                  placeholder="Write your comments (token holders only)"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <div className="comment-actions">
                  <span>{commentText.length}/256</span>
                  <button onClick={handleCommentSubmit}>Submit</button>
                </div>
              </div>
              {comments.map((comment, index) => (
                <div key={index} className="comment">
                  <div className="comment-header">
                    <span className="comment-author">{truncateAddress(comment.WalletAddress)}</span>
                    <span className="comment-time">{new Date(comment.Created_at).toLocaleString()}</span>
                  </div>
                  <p className="comment-content">{comment.Comment}</p>
                </div>
              ))}
              {comments.length > 0 && (
                <button
                  className="load-more-comments"
                  onClick={loadMoreComments}
                  disabled={loadingMoreComments}
                >
                  {loadingMoreComments ? 'Loading...' : 'Load More Comments'}
                </button>
              )}
            </div>
          )}

          {activeTab === 'trading' && (
            <div className="trading-history">
              <table>
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Type</th>
                    <th>$G7</th>
                    <th>{market.Symbol}</th>
                    <th>Date</th>
                    <th>Transaction</th>
                  </tr>
                </thead>
                <tbody>
                  {tradingHistory.map((item, index) => (
                    <tr key={index}>
                      <td>{truncateAddress(item.WalletAddress)}</td>
                      <td className={item.BuySell}>{item.BuySell}</td>
                      <td>{item.TokenPrice.toFixed(2)}</td>
                      <td>{item.TokenQty.toFixed(2)}</td>
                      <td>{new Date(item.Created_at).toLocaleString()}</td>
                      <td>{item.TransactionAddress}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {tradingHistory.length > 0 && (
                <button
                  className="load-more-button"
                  onClick={loadMoreTrades}
                  disabled={loadingMoreTrades}
                >
                  {loadingMoreTrades ? 'Loading...' : 'Load More Trades'}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="holder-distribution">
          <h3>Holder Distribution <span className="holder-count">516176</span></h3>
          <table>
            <thead>
              <tr>
                <th>Holder</th>
                <th>Tokens</th>
                <th>Percentage</th>
                <th>Points</th>
              </tr>
            </thead>
            <tbody>
              {holderDistribution.map((holder, index) => (
                <tr key={index}>
                  <td>{truncateAddress(holder.WalletAddress)}</td>
                  <td>{holder.TokenQty}</td>
                  <td>{percentageOfOwnership(holder.TokenQty, 1000000)}%{holder.IsCreator ? ' (Creator)' : ''}</td>
                  <td>{holder.Points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Modal isOpen={showSendToDevModal} onClose={() => setShowSendToDevModal(false)}>
        <h2>Send Tokens to this Games Developers</h2>
        <p>
          Enter the amount of <span>${market.Symbol}</span> you wish to send to the game developers:<br />
          <small>(token must be in connected wallet and dev must have claimed game)</small>
        </p>
        <input
          type="number"
          placeholder="Amount"
          value={sendDevAmount}
          onChange={(e) => setSendDevAmount(e.target.value)}
        />
        <button onClick={() => {
          sendToDeveloper();
          setShowSendToDevModal(false);
        }}>Send</button>
      </Modal>

      <Modal isOpen={showSendToFriendModal} onClose={() => setShowSendToFriendModal(false)}>
        <h2>Send Tokens to a Friend</h2>
        <p>
          Enter your friend's address and the amount of <span>${market.Symbol}</span> you wish to send:<br />
          <small>(token must be in connected wallet and friend must have a weirdly account)</small>
        </p>
        <input
          type="text"
          placeholder="Friend's Address"
          value={sendFriendAddress}
          onChange={(e) => setSendFriendAddress(e.target.value)}
        />
        <input
          type="number"
          placeholder="Amount"
          value={sendFriendAmount}
          onChange={(e) => setSendFriendAmount(e.target.value)}
        />
        <button onClick={() => {
          sendToFriend();
          setShowSendToFriendModal(false);
        }}>Send</button>
      </Modal>

      {/* Purchase Confirmation Modal */}
      <Modal isOpen={showPurchaseModal} onClose={() => setShowPurchaseModal(false)} shake={shakeModal}>
        <div className="purchase-confirmation">
          <h2>Congratulations!</h2>
          <p>You have successfully purchased:<br />{tradeAmount} {market?.Symbol}</p>
          <p>Your new balance: {userBalance} {market?.Symbol}</p>
          <button onClick={() => handleShareOnTwitter('bought')} className="share-twitter-btn">
            Share on X
          </button>
          <button onClick={() => setShowPurchaseModal(false)} className="close-modal-btn">
            Close
          </button>
        </div>
      </Modal>

      {/* Sell Confirmation Modal */}
      <Modal isOpen={showSellModal} onClose={() => setShowSellModal(false)} shake={shakeModal}>
        <div className="sell-confirmation">
          <h2>Sell Confirmed!</h2>
          <p>You have successfully sold:<br />{tradeAmount} {market?.Symbol}</p>
          <p>Your new balance: {userBalance} {market?.Symbol}</p>
          <button onClick={() => handleShareOnTwitter('sold')} className="share-twitter-btn">
            Share on Twitter
          </button>
          <button onClick={() => setShowSellModal(false)} className="close-modal-btn">
            Close
          </button>
        </div>
      </Modal>

      {/* Slippage Setting Modal */}
      <Modal isOpen={showSlippageModal} onClose={() => setShowSlippageModal(false)}>
        <div className="slippage-setting">
          <h2>Set Slippage Percentage</h2>
          <div className="slippage-buttons">
            <button onClick={() => handleSlippageChange(2)}>2%</button>
            <button onClick={() => handleSlippageChange(4)}>4%</button>
            <button onClick={() => handleSlippageChange(8)}>8%</button>
            <button onClick={() => handleSlippageChange(10)}>10%</button>
          </div>
        </div>
      </Modal>

      <div className="toast-container">
        {toasts.map(toast => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

    </div>
  );
};

export default TokenPage;